import React, { createContext, useContext, useState, useCallback, ReactNode, useEffect } from 'react';
import { setDatabaseErrorHandler } from '../services/http';

interface DatabaseErrorContextType {
    hasDatabaseError: boolean;
    errorMessage: string | null;
    clearError: () => void;
}

const DatabaseErrorContext = createContext<DatabaseErrorContextType | undefined>(undefined);

interface DatabaseErrorProviderProps {
    children: ReactNode;
}

export const DatabaseErrorProvider: React.FC<DatabaseErrorProviderProps> = ({ children }) => {
    // TEMPORARY: Set to true to test the database error page
    // TODO: Remove this before deploying to production
    const FORCE_DATABASE_ERROR = false;
    
    const [hasDatabaseError, setHasDatabaseError] = useState(FORCE_DATABASE_ERROR);
    const [errorMessage, setErrorMessage] = useState<string | null>(
        FORCE_DATABASE_ERROR ? 'Operation users.findOne() buffering timed out after 10000ms' : null
    );

    const handleDatabaseError = useCallback((error: string) => {
        setErrorMessage(error);
        setHasDatabaseError(true);
    }, []);

    const clearError = useCallback(() => {
        setHasDatabaseError(false);
        setErrorMessage(null);
    }, []);

    useEffect(() => {
        // Register the error handler with the HTTP service
        setDatabaseErrorHandler(handleDatabaseError);
        
        return () => {
            // Cleanup: unregister handler
            setDatabaseErrorHandler(null);
        };
    }, [handleDatabaseError]);

    return (
        <DatabaseErrorContext.Provider
            value={{
                hasDatabaseError,
                errorMessage,
                clearError,
            }}
        >
            {children}
        </DatabaseErrorContext.Provider>
    );
};

export const useDatabaseError = (): DatabaseErrorContextType => {
    const context = useContext(DatabaseErrorContext);
    if (!context) {
        throw new Error('useDatabaseError must be used within a DatabaseErrorProvider');
    }
    return context;
};

